/**
 * Automatically rotates a product card's image through all of its variant
 * photos while the card is visible on screen, starting after a short pause
 * on the main image so the product is identifiable first. Pauses when the
 * card scrolls out of view and resumes (from the main image again) when it
 * scrolls back in. Works identically on desktop and mobile — no hover or
 * touch interaction required.
 *
 * Only activates for cards with more than one variant image already present
 * in the DOM (see snippets/card-gallery.liquid) — no extra network requests
 * beyond the browser lazily resolving <img> tags already in the page.
 *
 * Note: this intentionally does NOT use slideshow-component's .select()/.next().
 * That component only initializes its scroll engine when more than one slide
 * is visible on connect; card-gallery renders every variant slide hidden except
 * the currently-shown one, so .select() is a permanent no-op for these cards.
 * Toggling the [hidden] attribute directly (the same mechanism the component
 * uses for its own default state) works regardless.
 */
(function () {
  var ROTATE_INTERVAL_MS = 1800;
  var INITIAL_DELAY_MS = 2500;
  var VISIBILITY_THRESHOLD = 0.2;
  var reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function setupCard(card) {
    if (!card || card.dataset.variantRotateReady) return;

    var allSlides = Array.prototype.slice.call(card.querySelectorAll('slideshow-slide'));
    if (allSlides.length < 2) return;

    var variantSlides = allSlides.filter(function (slide) {
      return slide.hasAttribute('variant-image');
    });
    if (variantSlides.length < 2 || reducedMotion) return;

    // The slide actually shown on page load — may or may not be one of the
    // variant-image slides — is what the card must rest on between cycles.
    var defaultSlide = null;
    for (var i = 0; i < allSlides.length; i++) {
      if (!allSlides[i].hidden) {
        defaultSlide = allSlides[i];
        break;
      }
    }
    if (!defaultSlide) defaultSlide = allSlides[0];

    card.dataset.variantRotateReady = 'true';

    function show(slide) {
      for (var i = 0; i < allSlides.length; i++) {
        var isTarget = allSlides[i] === slide;
        allSlides[i].hidden = !isTarget;
        allSlides[i].setAttribute('aria-hidden', isTarget ? 'false' : 'true');
      }
    }

    var startTimeoutId = null;
    var intervalId = null;
    var cursor = -1;

    function stop() {
      if (startTimeoutId) {
        window.clearTimeout(startTimeoutId);
        startTimeoutId = null;
      }
      if (intervalId) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      cursor = -1;
      show(defaultSlide);
    }

    function start() {
      if (startTimeoutId || intervalId) return;
      startTimeoutId = window.setTimeout(function () {
        startTimeoutId = null;
        intervalId = window.setInterval(function () {
          cursor = (cursor + 1) % variantSlides.length;
          show(variantSlides[cursor]);
        }, ROTATE_INTERVAL_MS);
      }, INITIAL_DELAY_MS);
    }

    if (!('IntersectionObserver' in window)) {
      // No IntersectionObserver support: fall back to always-on rotation.
      start();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        for (var e = 0; e < entries.length; e++) {
          if (entries[e].isIntersecting) start();
          else stop();
        }
      },
      { threshold: VISIBILITY_THRESHOLD }
    );

    observer.observe(card);
  }

  function setupAllCards(root) {
    var scope = root || document;
    var cards = scope.querySelectorAll('product-card');
    for (var i = 0; i < cards.length; i++) setupCard(cards[i]);
  }

  function init() {
    setupAllCards();

    if (!window.MutationObserver) return;

    var observer = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var addedNodes = mutations[m].addedNodes;
        for (var n = 0; n < addedNodes.length; n++) {
          var node = addedNodes[n];
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches && node.matches('product-card')) setupCard(node);
          setupAllCards(node);
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
